<template>
  <!-- Aufbau bewusst identisch zu den übrigen Seiten: ein schlichtes `div`,
       darin `page-header` mit `page-leftheader`/`headline`. Vorher stand hier
       ein zusätzliches `container-fluid` und der Erklärtext lag INNERHALB des
       Kopfbereichs — beides verschob die Überschrift gegenüber jeder anderen
       Seite, obwohl die Schrift selbst schon stimmte.

       Zur Auszeichnung: `.page-title` gibt 20 px (style.css), `.text-primary`
       das Violett #664dc9 (main.css, unter `.v-application` — nur diese
       Verschachtelung schlägt Bootstraps Blau). Ganz früher stand hier
       `h2.main-content-title.tx-24.mg-b-5`; diese drei Klassen sind im gesamten
       Stilvorrat nirgends definiert, das Element war also ein nacktes `h2`. -->
  <div>
    <div class="page-header">
      <div class="page-leftheader">
        <div class="headline">
          <!-- Die Anzahl steht bewusst nicht hier, sondern unten an der
               Überschrift „Übersicht" der Tabelle. Dort gehört sie hin: Sie
               beschreibt, wie viele Zeilen in dieser Liste stehen — nicht, was
               die Seite ist. -->
          <h4 class="page-title text-primary">Gruppen</h4>
          <!-- Der Erklärtext steht innerhalb von `headline`, direkt unter der
               Überschrift — genau wie auf der Meldungen-Seite. Vorher stand er
               außerhalb des Kopfbereichs und brauchte einen selbstgebauten
               Abstand, der diese Seite gegen alle anderen verschob.

               `page-subtitle` statt `text-muted`: Dieselbe Klasse benutzen auch
               Rundnachricht, Neuigkeiten und Meldungen. Über eine gemeinsame
               Klasse wirkt jede spätere Änderung überall zugleich — vorher
               hätte man vier Stellen anfassen müssen und die vierte übersehen. -->
          <p class="page-subtitle mb-0">
            Alle Gruppen der App mit ihren Mitgliedern. Ein Klick auf eine Zeile
            zeigt, wer darin ist.
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h3 class="card-title mb-0">
              Übersicht
              <span v-if="total > 0" class="badge badge-primary ms-2">{{ total }}</span>
            </h3>
            <div class="search-box">
              <input
                type="text"
                class="form-control"
                placeholder="Gruppe suchen …"
                v-model="search"
                @input="onSearchInput"
              />
            </div>
          </div>

          <div class="card-body">
            <!-- Ladezustand, Leerzustand und Fehlerzustand sind bewusst drei
                 verschiedene Anzeigen. Auf den übrigen Seiten des Dashboards
                 sehen ein fehlgeschlagener Abruf und "es gibt nichts" gleich
                 aus — man sucht dann einen Fehler in den Daten, obwohl der
                 Server gar nicht geantwortet hat. -->
            <div v-if="loading && crews.length === 0" class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-3 text-muted mb-0">Gruppen werden geladen …</p>
            </div>

            <div v-else-if="loadError" class="alert alert-danger mb-0">
              <strong>Die Gruppen konnten nicht geladen werden.</strong>
              <div class="mt-1">{{ loadError }}</div>
              <button class="btn btn-sm btn-outline-danger mt-2" @click="reload">
                Erneut versuchen
              </button>
            </div>

            <div v-else-if="crews.length === 0" class="text-center py-5 text-muted">
              <p class="mb-0" v-if="search">
                Keine Gruppe gefunden, die „{{ search }}“ enthält.
              </p>
              <p class="mb-0" v-else>Es gibt noch keine Gruppen.</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover card-table table-vcenter text-nowrap">
                <thead>
                  <tr>
                    <th style="width: 104px"></th>
                    <th>Name</th>
                    <th>Gruppenleitung</th>
                    <th class="text-center">Mitglieder</th>
                    <th class="text-center">Sichtbarkeit</th>
                    <th>Angelegt</th>
                    <th class="text-end" style="width: 140px">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="crew in crews"
                    :key="crew._id"
                    class="crew-row"
                    @click="openMembers(crew)"
                  >
                    <td>
                      <img
                        class="crew-image"
                        :src="$img(crew.image, $awsLargeImages)"
                        :alt="crew.name"
                      />
                    </td>
                    <td>
                      <!--
                        WAR FALSCH: Nur die Beschreibung darunter hatte eine
                        Höchstbreite mit Abschneiden — der Name selbst nicht.
                        Die Tabelle steht auf text-nowrap; ein einzelner sehr
                        langer Gruppenname sprengte damit die ganze Zeile und
                        schob alle folgenden Spalten (Mitglieder, Sichtbarkeit,
                        Angelegt, Aktion) aus dem sichtbaren Bereich. Dieselbe
                        Klasse wie bei der Beschreibung, damit beide Zeilen
                        gleich breit bleiben und lang umbrechen statt zu
                        sprengen.
                      -->
                      <div class="fw-semibold crew-name">{{ crew.name || '(ohne Name)' }}</div>
                      <div class="text-muted tx-12 crew-description">
                        {{ crew.description }}
                      </div>
                    </td>
                    <td>
                      <div v-if="crew.leader && crew.leader.userName" class="d-flex align-items-center">
                        <img
                          class="leader-image me-2"
                          :src="$img(crew.leader.image, $awsLargeImages)"
                          :alt="crew.leader.userName"
                        />
                        <span>{{ crew.leader.userName }}</span>
                      </div>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-center">
                      <span class="badge badge-primary">{{ crew.memberCount }}</span>
                      <!-- Offene Anfragen stehen nur dann da, wenn es welche
                           gibt. Eine dauerhafte "0" neben jeder Zeile liest
                           sich wie ein Fehler. -->
                      <span
                        v-if="crew.candidateCount > 0"
                        class="badge badge-warning ms-1"
                        :title="crew.candidateCount + ' offene Beitrittsanfrage(n)'"
                      >
                        +{{ crew.candidateCount }}
                      </span>
                    </td>
                    <td class="text-center">
                      <!-- `isActive` wurde vom Server bereits mitgeliefert, aber
                           nirgends angezeigt: Eine deaktivierte Gruppe sah damit
                           exakt aus wie eine aktive. Sie steht zuerst, weil sie
                           die wichtigere Aussage ist — eine deaktivierte Gruppe
                           ist für Mitglieder gar nicht mehr da. -->
                      <span v-if="!crew.isActive" class="badge badge-danger">deaktiviert</span>
                      <span v-else-if="crew.isPublic" class="badge badge-success">öffentlich</span>
                      <span v-else class="badge badge-secondary">privat</span>
                    </td>
                    <td>{{ formatDate(crew.createdAt) }}</td>
                    <td class="text-end">
                      <button
                        class="btn btn-sm btn-outline-primary crew-action"
                        @click.stop="openMembers(crew)"
                      >
                        Mitglieder
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="crews.length < total" class="text-center mt-3">
                <button class="btn btn-outline-primary" :disabled="loading" @click="loadMore">
                  {{ loading ? 'Lädt …' : `Weitere laden (${crews.length} von ${total})` }}
                </button>
              </div>
              <div v-else-if="total > 0" class="text-center mt-3 text-muted tx-12">
                Alle {{ total }} Gruppen geladen.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mitgliederliste -->
    <div
      class="modal fade"
      :class="{ show: showMembers }"
      :style="{ display: showMembers ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title d-flex align-items-center">
              <img
                v-if="selectedCrew"
                class="crew-image me-2"
                :src="$img(selectedCrew.image, $awsLargeImages)"
                :alt="selectedCrew.name"
              />
              <span>{{ selectedCrew ? selectedCrew.name : '' }}</span>
            </h5>
            <button type="button" class="btn-close" @click="closeMembers"></button>
          </div>

          <div class="modal-body">
            <div v-if="membersLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-3 text-muted mb-0">Mitglieder werden geladen …</p>
            </div>

            <div v-else-if="membersError" class="alert alert-danger mb-0">
              {{ membersError }}
            </div>

            <div v-else>
              <p v-if="detail && detail.description" class="text-muted">
                {{ detail.description }}
              </p>

              <h6 class="mt-3">
                Mitglieder
                <span class="badge badge-primary">{{ members.length }}</span>
              </h6>

              <div v-if="members.length === 0" class="text-muted">
                Diese Gruppe hat keine Mitglieder.
              </div>

              <table v-else class="table table-sm table-vcenter">
                <thead>
                  <tr>
                    <th style="width: 50px"></th>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th class="text-end">Kilometer</th>
                    <th class="text-center">Rolle</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Gesperrte Mitglieder sieht NUR die Verwaltung, und dort
                       ausdrücklich gekennzeichnet. In der App werden sie
                       herausgefiltert (`crewController.js`, `crewDetails`).
                       Ohne Kennzeichen sah es hier wie ein Anzeigefehler aus:
                       In "Kurvenjäger KW" standen zwei Namen doppelt, weil es
                       je ein altes gesperrtes und ein neues aktives Konto
                       gibt. -->
                  <tr
                    v-for="member in members"
                    :key="member._id"
                    :class="{ 'member-blocked': !isActive(member) }"
                  >
                    <td>
                      <img
                        class="member-image"
                        :src="$img(member.image, $awsLargeImages)"
                        :alt="member.userName"
                      />
                    </td>
                    <td>
                      {{ member.userName }}
                      <span v-if="!isActive(member)" class="badge badge-danger ms-1">Gesperrt</span>
                    </td>
                    <td class="text-muted tx-12">{{ member.email }}</td>
                    <td class="text-end">{{ formatKm(member.drivenKilometers) }}</td>
                    <td class="text-center">
                      <span v-if="member.isLeader" class="badge badge-warning">Leitung</span>
                      <span v-else class="text-muted">Mitglied</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Offene Beitrittsanfragen stehen bewusst getrennt darunter.
                   Wer sie mit in die Mitgliederliste nimmt, erzeugt eine Zahl,
                   die nicht mehr zur Übersicht passt. -->
              <div v-if="candidates.length > 0" class="mt-4">
                <h6>
                  Offene Beitrittsanfragen
                  <span class="badge badge-warning">{{ candidates.length }}</span>
                </h6>
                <table class="table table-sm table-vcenter">
                  <tbody>
                    <tr v-for="candidate in candidates" :key="candidate._id">
                      <td style="width: 50px">
                        <img
                          class="member-image"
                          :src="$img(candidate.image, $awsLargeImages)"
                          :alt="candidate.userName"
                        />
                      </td>
                      <td>{{ candidate.userName }}</td>
                      <td class="text-muted tx-12">{{ candidate.email }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeMembers">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Der Abdunkler muss zwischen Kopfzeile und Dialog liegen: über der
         Vorlagen-Kopfzeile (z-index 999999), damit sie mit abgedunkelt wird,
         aber unter dem Dialog selbst. -->
    <div
      v-if="showMembers"
      class="modal-backdrop fade show crew-backdrop"
      @click="closeMembers"
    ></div>
  </div>
</template>

<script>
const PAGE_SIZE = 25;

export default {
  name: 'Crews',

  // Alle Felder sind hier vorab angelegt. Vue 2 macht Eigenschaften, die erst
  // spaeter entstehen, nicht beobachtbar — die Startseite hatte genau deshalb
  // zwoelf Kacheln, die dauerhaft leer blieben, obwohl die Zahlen ankamen.
  data() {
    return {
      crews: [],
      total: 0,
      // Bestimmt den nächsten Startpunkt beim Server. Bewusst NICHT aus
      // crews.length berechnet — siehe fetchCrews().
      skip: 0,
      loading: false,
      loadError: '',
      search: '',
      searchTimer: null,

      showMembers: false,
      selectedCrew: null,
      detail: null,
      members: [],
      candidates: [],
      membersLoading: false,
      membersError: '',
    };
  },

  mounted() {
    this.reload();
    // Der Dialog ist hier von Hand gebaut, nicht über Vuetify. Damit fehlen
    // zwei Dinge, die man von jedem anderen Dialog im Dashboard gewohnt ist:
    // ESC zum Schließen und eine Seite dahinter, die stillhält. Beides wird
    // unten nachgereicht.
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    // Ohne das laeuft der Zeitgeber nach dem Verlassen der Seite weiter und
    // stellt eine Anfrage fuer eine Komponente, die es nicht mehr gibt.
    if (this.searchTimer) clearTimeout(this.searchTimer);
    document.removeEventListener('keydown', this.onKeydown);
    // Falls die Seite mit offenem Dialog verlassen wird: Sperre lösen, sonst
    // laesst sich das Dashboard danach gar nicht mehr scrollen.
    document.body.classList.remove('modal-open');
  },

  methods: {
    // Die Suche wartet 300 ms. Ohne diese Bremse stellt jeder Tastendruck eine
    // eigene Anfrage — auf der Meldungen-Seite ist genau das der Fall.
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 300);
    },

    reload() {
      this.crews = [];
      this.total = 0;
      this.skip = 0;
      this.fetchCrews();
    },

    loadMore() {
      this.fetchCrews();
    },

    fetchCrews() {
      const _this = this;
      this.loading = true;
      this.loadError = '';

      // WAR FALSCH: skip wurde aus crews.length berechnet. Legt jemand
      // während des Blätterns eine neue Gruppe an, rutscht die gesamte,
      // server-seitig nach Datum sortierte Liste um eine Position — der
      // Datensatz an der alten Grenze taucht dann ein zweites Mal auf und
      // wird durch die Entdopplung herausgefiltert. crews.length wuchs
      // dadurch langsamer als der Server tatsächlich Einträge geliefert
      // hatte, der berechnete skip blieb irgendwann auf einem festen Wert
      // stehen (in einem nachgestellten Fall: 30 bei total 31) — jeder
      // weitere Klick auf "Weitere laden" fragte exakt dieselbe, bereits
      // bekannte Seite erneut ab und brachte nie wieder etwas Neues. Die
      // wirklich neue Gruppe blieb dauerhaft unsichtbar, nur ein Neuladen
      // der Seite (F5) half.
      //
      // skip zählt jetzt separat und rückt bei JEDER Antwort um die Anzahl
      // der tatsächlich vom Server gelieferten Einträge weiter — unabhängig
      // davon, wie viele davon nach der Entdopplung übrig bleiben. Damit
      // bewegt sich der Startpunkt garantiert vorwärts und der Vorgang endet
      // immer, auch wenn dabei (bei einer Verschiebung mitten im Blättern)
      // im schlechtesten Fall ein einzelner, zwischenzeitlich neu
      // angelegter Eintrag erst beim nächsten Neuladen der Seite erscheint —
      // eine vollständig lückenlose Paginierung bräuchte einen stabilen
      // Cursor (z. B. "vor diesem Datum") auf der Serverseite.
      const skip = this.skip;
      const query = this.search ? `?search=${encodeURIComponent(this.search)}` : '';

      this.$axios
        .get(`/admin/crews/${skip}/${PAGE_SIZE}${query}`)
        .then((response) => {
          _this.loading = false;

          // HTTP 200 heisst hier nicht Erfolg: helpers/response.js antwortet
          // auch bei fachlichem Fehlschlag mit 200 und schreibt das Ergebnis
          // nur in `success`. Fast alle anderen Seiten pruefen das nicht und
          // melden deshalb Erfolg, wenn nichts passiert ist.
          if (response.data && response.data.success === false) {
            _this.loadError = response.data.message || 'Der Server hat die Anfrage abgelehnt.';
            return;
          }

          const payload = (response.data && response.data.data) || {};
          const neue = payload.crews || [];
          _this.skip = skip + neue.length;

          // Anhaengen statt ersetzen, aber ohne Doppelte: Die Nutzerliste
          // zeigte beim ersten Nachladen dieselben Eintraege ein zweites Mal,
          // weil der Startpunkt vor dem Hochzaehlen berechnet wurde.
          const bekannt = new Set(_this.crews.map((c) => c._id));
          _this.crews = _this.crews.concat(neue.filter((c) => !bekannt.has(c._id)));

          if (neue.length === 0) {
            // Der Server meldet an dieser Stelle nichts mehr — auch wenn
            // `total` (wegen einer Verschiebung während des Blätterns) noch
            // höher stand, ist wirklich nichts mehr zu holen. Ohne dieses
            // Angleichen bliebe der Knopf "Weitere laden" für immer sichtbar
            // und unwirksam (siehe Kommentar oben).
            _this.total = _this.crews.length;
          } else {
            _this.total = typeof payload.total === 'number' ? payload.total : _this.crews.length;
          }
        })
        .catch((error) => {
          _this.loading = false;
          _this.loadError = _this.serverMessage(error);
          console.error(error);
        });
    },

    // ESC schließt den Dialog — wie bei jedem Vuetify-Dialog im Dashboard.
    onKeydown(event) {
      if (event.key === 'Escape' && this.showMembers) this.closeMembers();
    },

    openMembers(crew) {
      this.selectedCrew = crew;
      this.showMembers = true;
      // Sperrt die Seite dahinter. Ohne das schiebt das Mausrad die Tabelle
      // unter dem Dialog weg, und nach dem Schließen steht man an einer
      // anderen Stelle als vorher.
      document.body.classList.add('modal-open');
      this.detail = null;
      this.members = [];
      this.candidates = [];
      this.membersError = '';
      this.membersLoading = true;

      const _this = this;
      this.$axios
        .get(`/admin/crew/${crew._id}/members`)
        .then((response) => {
          _this.membersLoading = false;

          if (response.data && response.data.success === false) {
            _this.membersError = response.data.message || 'Die Mitglieder konnten nicht geladen werden.';
            return;
          }

          const payload = (response.data && response.data.data) || {};
          _this.detail = payload;
          _this.members = payload.members || [];
          _this.candidates = payload.candidates || [];
        })
        .catch((error) => {
          _this.membersLoading = false;
          _this.membersError = _this.serverMessage(error);
          console.error(error);
        });
    },

    closeMembers() {
      this.showMembers = false;
      this.selectedCrew = null;
      document.body.classList.remove('modal-open');
    },

    serverMessage(error, fallback) {
      const standard = fallback || 'Die Anfrage ist fehlgeschlagen.';
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      if (error && error.message) return error.message;
      return standard;
    },

    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    // „0 km" und „unbekannt" sind zweierlei.
    //
    // Vorher fielen beide auf „—" zusammen: `n <= 0` warf einen echten Nullwert
    // mit einem fehlenden in denselben Topf. Das Nutzermodell setzt
    // `drivenKilometers` aber auf 0.0 vor — jedes frisch angelegte Mitglied ist
    // tatsächlich bei null gefahren, und der Verwalter konnte das nicht mehr von
    // „wir wissen es nicht" unterscheiden.

    // Aktiv ist NUR status 1.
    //
    // Bewusst eine Einschlussliste statt "ungleich 2": Das System kennt laut
    // `helpers/constants.js` genau ACTIVE (1) und DEACTIVE (2) — im Bestand
    // liegen aber 14 Konten mit `status: 0`, einem Wert, den der Code gar nicht
    // vorsieht. Eine Ausschlussliste haette die faelschlich als aktiv gezeigt.
    isActive(member) {
      return Number(member && member.status) === 1;
    },
    formatKm(value) {
      if (value === null || value === undefined || value === '') return '—';
      const n = Number(value);
      if (!isFinite(n)) return '—';
      if (n <= 0) return '0 km';
      return `${Math.round(n).toLocaleString('de-DE')} km`;
    },
  },
};
</script>

<style scoped>
.crew-row {
  cursor: pointer;
}

/* Querformat statt Quadrat: Gruppenbilder sind Landschafts- und
   Fahrzeugaufnahmen, bei denen ein quadratischer Ausschnitt die Bildmitte
   wegschneidet. 88×62 zeigt genug, um die Gruppe wiederzuerkennen, ohne die
   Zeilenhöhe der Tabelle zu sprengen. */
.crew-image {
  width: 88px;
  height: 62px;
  border-radius: 8px;
  object-fit: cover;
  background-color: rgba(255, 255, 255, 0.04);
}

/* Im Dialogkopf bleibt es kleiner — dort steht es neben der Überschrift und
   soll sie nicht überragen. */
.modal-title .crew-image {
  width: 52px;
  height: 38px;
}

.leader-image,
.member-image {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

/* Lange Beschreibungen sollen die Zeilenhoehe nicht sprengen. Die Tabelle
   steht auf `text-nowrap`, ohne Begrenzung schiebt ein langer Text die
   uebrigen Spalten aus dem Bild. */
.crew-description {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Der Name bekam bisher keine Begrenzung — anders als die Beschreibung
   darunter. Ein einzelner sehr langer Gruppenname sprengte damit die ganze
   Tabellenzeile (die Tabelle steht auf text-nowrap) und schob Mitglieder,
   Sichtbarkeit, Angelegt und Aktion aus dem sichtbaren Bereich. Anders als
   bei der Beschreibung wird hier umgebrochen statt abgeschnitten: Der Name
   ist die Kennung der Gruppe, ihn stumm zu kappen wäre die schlechtere
   Wahl. */
.crew-name {
  max-width: 320px;
  /* !important nötig: Die Tabelle steht auf Bootstraps .text-nowrap, das
     seinerseits mit !important arbeitet (public/css/style.css) — eine
     einfache Regel würde hier stillschweigend verlieren und der Umbruch
     bliebe wirkungslos. */
  white-space: normal !important;
  word-break: break-word;
}

.search-box {
  width: 260px;
  max-width: 100%;
}

/* Hier stand eine Regel `.crew-hinweis` mit negativem oberen Abstand — nötig,
   solange der Erklärtext außerhalb des Kopfbereichs saß. Er steht jetzt darin,
   wie auf der Meldungen-Seite, und braucht keine Sonderbehandlung mehr. */

/* Die Anzahl neben „Übersicht" soll die Überschrift nicht überragen: Die
   Kartentitel sind auf Großschreibung gestellt, ein Abzeichen in derselben
   Höhe wirkte sonst wie ein weiteres Wort.

   `white-space: nowrap` am Titel verhindert, dass die Zahl auf schmalen
   Bildschirmen unter das Wort rutscht — bei 375 px stand sie sonst allein in
   der zweiten Zeile und sah aus wie ein verirrter Wert. */
.card-title {
  white-space: nowrap;
}

.card-title .badge {
  font-size: 12px;
  vertical-align: middle;
  text-transform: none;
}

/* Der Kopf der Karte trägt Überschrift und Suchfeld nebeneinander. Auf schmalen
   Bildschirmen brauchen sie untereinander Platz, sonst quetscht das Suchfeld die
   Überschrift zusammen. */
@media (max-width: 768px) {
  .card-header {
    flex-wrap: wrap;
    gap: 12px;
  }

  .search-box {
    width: 100%;
  }
}

.crew-backdrop {
  z-index: 1000000;
}

/* Der Knopf wurde vom Tabellenlayout zusammengedrückt: gemessen 44 px breit,
   während der Text „Mitglieder" 70 px braucht — er lief also über und war
   abgeschnitten. Die übrigen Spalten stehen auf `text-nowrap` und beanspruchen
   ihre volle Breite zuerst; was übrig bleibt, bekommt die letzte Spalte.
   `min-width: max-content` nimmt den Knopf von dieser Rechnung aus: Er
   behält immer mindestens die Breite seines eigenen Textes. */
.crew-action {
  min-width: max-content;
  white-space: nowrap;
}

/* Der Dialog wird hier von Hand auf- und zugeschaltet, nicht über Bootstraps
   eigenes JavaScript. Das hat eine Folge, die man erst auf einem kleineren
   Bildschirm merkt: Bootstrap setzt beim Öffnen selbst `overflow-y: auto` auf
   den Rahmen. Ohne diesen Schritt bleibt die Vorgabe `hidden` stehen — ist die
   Mitgliederliste dann höher als das Fenster, lässt sich der Dialog weder
   scrollen noch schieben, und der Kopf mit dem Gruppennamen ist gar nicht
   erreichbar. */
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  overflow-x: hidden;
  /* Die Kopfzeile der gekauften Vorlage (`app-header`, public/css/style.css)
     liegt auf `z-index: 999999` — gemessen, nicht geschätzt. Bootstraps Dialog
     steht standardmäßig auf 1050 und verschwindet damit unter der Kopfzeile:
     Ihre oberen 67 Pixel überdecken den Dialogkopf samt Gruppenname und
     Schließen-Kreuz, und das Suchfeld der Kopfzeile fängt Klicks ab, die dem
     Dialog gelten. Deshalb hier darüber. Die Vorlage bleibt unangetastet —
     sie hat 52.522 Zeilen und wird bei einem Aktualisieren überschrieben. */
  z-index: 1000001;
}

/* Der Dialog beginnt oben mit Abstand und wächst nach unten mit. Ohne die
   Begrenzung auf die Fensterhöhe wächst der Inhalt über den Bildrand hinaus,
   statt eine eigene Bildlaufleiste zu bekommen. */
.modal.show .modal-dialog {
  margin: 1.75rem auto;
  max-height: calc(100vh - 3.5rem);
}

.modal.show .modal-content {
  max-height: calc(100vh - 3.5rem);
}

/* Kopf und Fuß bleiben stehen, nur die Mitgliederliste dazwischen läuft.
   So bleibt sichtbar, welche Gruppe man gerade ansieht, auch wenn man weit
   nach unten gescrollt hat. */
.modal.show .modal-header,
.modal.show .modal-footer {
  flex-shrink: 0;
}

.modal.show .modal-body {
  overflow-y: auto;
}

/* Gesperrte Mitglieder deutlich zurücknehmen — sie sind Bestandsinformation
   für die Verwaltung, nicht der Normalfall. Blasser Text plus rotes Kennzeichen
   daneben; die Zeile bleibt lesbar, drängt sich aber nicht auf. */
.member-blocked {
  opacity: 0.55;
}
.member-blocked td {
  text-decoration: line-through;
  text-decoration-color: rgba(220, 53, 69, 0.5);
}
.member-blocked .badge {
  text-decoration: none;
  opacity: 1;
}
</style>
